import { motion } from "framer-motion";
import { useForm } from "react-hook-form";

import { Card } from "@/components/card";
import { Button } from "@/components/ui/button";
import { Form } from "@/components/ui/form";
import { queryClient } from "@/lib/react-query";
import { HttpError, isBadRequest } from "@/types/error";
import { PublicAnnouncement } from "@classview/shared-types";
import { zodResolver } from "@hookform/resolvers/zod";

import { announcementsQueryKey } from "../api/get-announcements";
import { useUpdateAnnouncement } from "../api/update-announcement";
import {
  AddAnnouncementFormData,
  addAnnouncementSchema,
} from "../types/add-announcement-schema";

import { AnnouncementElement } from "./announce.element";

type UpdateAnnouncementFormProps = {
  initialData: PublicAnnouncement;
};

const transformApiDataToFormData = (
  data: PublicAnnouncement,
): AddAnnouncementFormData => {
  return {
    type: data.type,
    title: data.title,
    message: data.message,
    date: {
      from: new Date(data.startDate),
      to: new Date(data.endDate),
    },
    organisations: data.organisations.map((organisation) => ({
      value: organisation.id,
      label: organisation.name,
    })),
  };
};

export function UpdateAnnouncementForm(props: UpdateAnnouncementFormProps) {
  const { initialData } = props;
  const form = useForm<AddAnnouncementFormData>({
    resolver: zodResolver(addAnnouncementSchema),
    defaultValues: transformApiDataToFormData(initialData),
  });

  const { mutateAsync: updateAnnouncement } = useUpdateAnnouncement({
    onSuccess: (result) => {
      queryClient.setQueryData(
        announcementsQueryKey,
        (oldData: PublicAnnouncement[] | undefined) => {
          if (!oldData) return oldData;
          return oldData.map((announcement) =>
            announcement.id === result.id ? result : announcement,
          );
        },
      );
      form.reset(transformApiDataToFormData(result));
    },
  });

  async function handleUpdateAnnouncement(data: AddAnnouncementFormData) {
    try {
      await updateAnnouncement({
        id: initialData.id,
        announcement: {
          type: data.type,
          title: data.title,
          message: data.message,
          startDate: data.date.from.toISOString(),
          endDate: data.date.to.toISOString(),
          // If no organisation IDs are provided, the announcement is global
          isGlobal: !data.organisations || data.organisations.length === 0,
          organisationIds:
            !data.organisations || data.organisations.length === 0
              ? undefined
              : data.organisations.map((organisation) => organisation.value),
        },
      });
    } catch (error) {
      const httpError = error as HttpError;
      if (isBadRequest(httpError)) {
        form.setError("root", { message: httpError.message.join(",") });
      } else {
        console.error("Unexpected error:", error);
        form.setError("root", {
          message: "An unexpected error occurred. Please try again.",
        });
      }
    }
  }

  return (
    <Form {...form}>
      <Card>
        <form onSubmit={form.handleSubmit(handleUpdateAnnouncement)}>
          <Card.Header title="Announcement Details" />
          <Card.Content className="space-y-4">
            <AnnouncementElement />
          </Card.Content>
          <motion.div
            className="overflow-hidden"
            initial={{ opacity: 0, height: 0 }}
            animate={
              form.formState.isDirty
                ? { opacity: 1, height: "auto" }
                : { opacity: 0, height: 0 }
            }
            transition={{ duration: 0.3 }}
          >
            <Card.Footer className="flex gap-4 sm:justify-end">
              <Button
                type="button"
                variant="outline"
                onClick={() =>
                  form.reset(transformApiDataToFormData(initialData))
                }
              >
                Cancel
              </Button>

              <Button
                type="submit"
                isLoading={form.formState.isSubmitting}
                isAnimated
              >
                Update
              </Button>
            </Card.Footer>
          </motion.div>
        </form>
      </Card>
    </Form>
  );
}
